import { Component, AfterViewInit, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { AnyCatcher } from 'rxjs/internal/AnyCatcher';
import { AgGridToolsService } from 'src/app/common/ag-grid-table/services/ag-grid-tools.service';
import { TableCommunicationService } from 'src/app/common/ag-grid-table/services/table-communication.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-line-item-cell-editor',
  templateUrl: './line-item.component.html',
  styleUrls: ['./line-item.component.sass']
})
export class LineItemEditorComponent implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  public params: any;
  public value: any;
  public originalValue: any;
  public fieldName: string = '';
  public options: any = {};
  public optionLabel: string = 'label';
  public optionValue: string = 'value';
  public suggestions: any = [];
  private subscription: Subscription = new Subscription();

  lineItemForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private agGridToolsService: AgGridToolsService,
    private el: ElementRef, 
    private renderer: Renderer2,
    private tableCommunicationService: TableCommunicationService
  ){
    this.lineItemForm = this.fb.group({
      lineItems: this.fb.array([])
    });

        // Subscribe to line item options from the service
        this.subscription.add(
          this.tableCommunicationService.lineItemOptions$.subscribe(options => {
            if (options) {
              // Initialize options object if not already done
              if (!this.options['t']) this.options['t'] = [];
              if (!this.options['currencies']) this.options['currencies'] = [];
              if (!this.options['titles']) this.options['titles'] = [];
              if (!this.options['resources']) this.options['resources'] = [];
              
              // Only update if not already set from params
              if (!this.params?.resources) this.options['t'] = options.resources;
              if (!this.params?.currencies) this.options['currencies'] = options.currencies;
              if (!this.params?.titles) this.options['titles'] = options.titles;
              
            }
          })
        );
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  async agInit(params: any): Promise<void> {
    this.params = params;
    this.fieldName = this.params.colDef.headerName;
    this.value = this.params.value;
    this.originalValue = this.params.value;
    
    // Initialize options object if not already done
    if (!this.options['t']) this.options['t'] = [];
    if (!this.options['currencies']) this.options['currencies'] = [];
    if (!this.options['titles']) this.options['titles'] = [];
    if (!this.options['resources']) this.options['resources'] = [];
    
    // Use params if available (for backward compatibility)
    if (this.params.resources) {
      this.options['t'] = this.params.resources;
      this.options['resources'] = this.params.resources;
    }
    if (this.params.currencies) {
      this.options['currencies'] = this.params.currencies;
    }
    if (this.params.titles) {
      this.options['titles'] = this.params.titles;
    }
    while (this.lineItems.length > 0) {
      this.lineItems.removeAt(0);
    }

    // Pre-fill the form with existing line items
    if (this.value && Array.isArray(this.value) && this.value.length > 0) {
      
      for (let i = 0; i < this.value.length; i++) {
        const item = this.value[i];
        if (!item) {
          continue;
        }
        
        
        if (!item.t || !item.id) {
          continue;
        }
        
        let typeOption = null;
        if (item.t && item.t._id && this.options['t'] && this.options['t'].length > 0) {
          typeOption = this.options['t'].find((opt: any) => opt._id === item.t._id);
        }
        
        let idOption = null;
        if (item.id && item.id._id) {
          if (item.lineItemType === 'Currency' && this.options['currencies']) {
            idOption = this.options['currencies'].find((opt: any) => opt._id === item.id._id);
          } else if (item.lineItemType === 'Title' && this.options['titles']) {
            idOption = this.options['titles'].find((opt: any) => opt._id === item.id._id);
          } else if (item.lineItemType === 'Resource' && this.options['resources']) {
            idOption = this.options['resources'].find((opt: any) => opt._id === item.id._id);
          } else if (item.lineItemType === 'Item') {
            idOption = item.id;
          }
        }
        const typeValue = typeOption || {
          _id: item.t._id || '',
          id: item.t.id || '',
          name: item.t.name || ''
        };
        
        const idValue = idOption || {
          _id: item.id._id || '',
          id: item.id.id || '',
          name: item.id.name || ''
        };
        
        const lineItemGroup = this.fb.group({
          type: [typeValue],
          id: [idValue],
          count: [item.c || 1, [Validators.required, Validators.min(1)]]
        });
        
        this.lineItems.push(lineItemGroup);
      }
    } 
    
    if (this.lineItems.length === 0) {
      if (this.params.isChallengePrize) {
        this.addLineItem();
        this.addLineItem();
      } else {
        this.addLineItem();
      }
    }
  }

  get lineItems() {
    return (this.lineItemForm.get('lineItems') as FormArray);
  }

  addLineItem() {
    let defaultValues = {
        type: {
          _id: '',
          id: '',
          name: 'Currency'  // Set default type to Currency
        },
        id: {
          _id: '',
          id: '',
          name: ''
        },
        count: 1  // Initialize with 1 instead of 0
    };

    console.log('Initial defaultValues:', defaultValues);

    // Try to find the Currency type in resources and set it if available
    if (this.options['t'] && Array.isArray(this.options['t']) && this.options['t'].length > 0) {
        let currencyTypeOption = this.options['t'].find((opt: any) => opt.name === 'Currency');
        console.log('Found currency type option:', currencyTypeOption);
        if (currencyTypeOption) {
            defaultValues.type = currencyTypeOption;
        }
    }

    const lineItemGroup = this.fb.group({
      type: [defaultValues.type],
      id: [defaultValues.id],
      count: [defaultValues.count, [Validators.required, Validators.min(1)]]
    });

    this.lineItems.push(lineItemGroup);
  }

  removeLineItem(index: number) {
    this.lineItems.removeAt(index);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const formGroupElement = this.el.nativeElement.querySelector('.line-item-fields');
      if (formGroupElement) {
        const firstInput = formGroupElement.querySelector('input, select');
        if (firstInput) {
          this.renderer.selectRootElement(firstInput).focus();
        }
      }
    }, 0);
  }

  getValue(): any {
    const formattedValues = [];

    // Iterate over lineItems FormArray
    for (let lineItem of this.lineItems.controls) {
      const lineItemValue = lineItem.value;
      
      if (!lineItemValue.type || !lineItemValue.id) {
        continue;
      }

      // Construct the desired object structure
      const formattedValue = {
        lineItemType: lineItemValue.type?.name,
        t: lineItemValue.type,
        id: lineItemValue.id,
        c: lineItemValue.count
      };

      formattedValues.push(formattedValue);
    }

    return formattedValues;
  }

  compareResource(c1: any, c2: any): boolean {
    if (!c1 || !c2) {
      return false;
    }
    
    if (c1._id && c2._id) {
      return c1._id === c2._id;
    }
    
    if (c1.id && c2.id) {
      return c1.id === c2.id;
    }
    
    return c1 === c2;
  }

  async getCurrencyOptions(event: any, index: Number){
    if(event.value && event.value.name){
      switch (event.value.name) {
        case 'Currency':
          break;
        default:
          break;
      }
    }
  }

  async getSuggestedItems(query: any,i: Number){
    this.suggestions = await this.agGridToolsService.getSuggestionsForRef(query, 'items');
  }

  getOptionByName(collection: string, name: string): any {
    if (!this.options[collection] || !Array.isArray(this.options[collection])) {
      return null;
    }
    return this.options[collection].find((opt: any) => opt.name === name);
  }
}
